/**
 * AdminAssetListPage — Issue #364.
 *
 * Lists administrative fixed assets with status / category filters, a basic
 * search box, and quick action buttons gated by frontend permission constants.
 *
 * 红线 (AI GUIDE §6 / §11):
 *   - 金额字段 (originalCost / depreciationAmount / residualValue) 仅在持有
 *     admin_assets:finance 权限时展示原值；其他角色看占位符。
 *   - 列表 / 详情 / 操作都走 generated api-client；不构造假数据。
 *   - 错误以 Card 形式展示；403 / 409 走统一文案。
 *   - 不渲染员工姓名、手机号、薪资等敏感字段；仅显示 employee id 摘要。
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'wouter';
import AccessDenied from '@/components/AccessDenied';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import {
  ADMIN_ASSETS_PERMISSIONS,
  hasPermission,
} from '@/lib/adminAssetsPermissions';
import {
  ADMIN_ASSET_CATEGORY_LABELS,
  ADMIN_ASSET_STATUS_LABELS,
  useAdminAssets,
  type AdminAssetDto,
} from '@/hooks/useAdminAssets';

function fmtDate(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('zh-CN');
}

function fmtMoney(value: string | null | undefined, canSee: boolean): string {
  if (!canSee) return '受限';
  if (value === null || value === undefined || value === '') return '—';
  return value;
}

const STATUS_BADGE_CLASS: Readonly<Record<AdminAssetDto['status'], string>> = {
  available: 'bg-emerald-100 text-emerald-700',
  assigned: 'bg-blue-100 text-blue-700',
  returned: 'bg-slate-100 text-slate-700',
  under_repair: 'bg-amber-100 text-amber-800',
  retired: 'bg-zinc-200 text-zinc-700',
  lost: 'bg-red-100 text-red-700',
  archived: 'bg-zinc-100 text-zinc-500',
};

export default function AdminAssetListPage() {
  const { permissions } = useAuth();
  const canRead = hasPermission(permissions, ADMIN_ASSETS_PERMISSIONS.READ);
  const canWrite = hasPermission(permissions, ADMIN_ASSETS_PERMISSIONS.WRITE);
  const canSeeFinance = hasPermission(
    permissions,
    ADMIN_ASSETS_PERMISSIONS.FINANCE,
  );

  const { items, total, loading, error, fetchAssets } = useAdminAssets();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<
    AdminAssetDto['status'] | ''
  >('');
  const [categoryFilter, setCategoryFilter] = useState<
    AdminAssetDto['category'] | ''
  >('');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [page, setPage] = useState(1);
  const limit = 20;

  useEffect(() => {
    if (!canRead) return;
    void fetchAssets({
      search: search || undefined,
      status: statusFilter || undefined,
      category: categoryFilter || undefined,
      includeArchived: includeArchived || undefined,
      page,
      limit,
    });
  }, [
    canRead,
    search,
    statusFilter,
    categoryFilter,
    includeArchived,
    page,
    fetchAssets,
  ]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / limit)),
    [total],
  );

  if (!canRead) return <AccessDenied resource="行政固定资产" />;

  return (
    <div className="space-y-4 p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">行政固定资产</h1>
          <p className="text-sm text-slate-500">
            登记电脑、工位、门禁等行政资产，维护领用 / 归还 /
            报修 / 报废 / 遗失 / 归档全流程，并与离职案件资产归还检查联动。
          </p>
        </div>
        {canWrite ? (
          <Link
            href="/admin/assets/new"
            className="rounded-md border px-4 py-2 text-sm hover:bg-muted"
            data-testid="admin-assets-create-entry"
          >
            登记资产 →
          </Link>
        ) : null}
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm text-slate-600">
          搜索
          <input
            type="text"
            value={search}
            placeholder="资产编码或名称"
            className="rounded border border-slate-300 px-2 py-1 text-sm"
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            data-testid="admin-assets-search-input"
          />
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          状态
          <select
            className="rounded border border-slate-300 px-2 py-1 text-sm"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(
                (e.target.value as AdminAssetDto['status']) || '',
              );
              setPage(1);
            }}
            data-testid="admin-assets-status-filter"
          >
            <option value="">全部</option>
            {(
              Object.entries(ADMIN_ASSET_STATUS_LABELS) as [
                AdminAssetDto['status'],
                string,
              ][]
            ).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          类别
          <select
            className="rounded border border-slate-300 px-2 py-1 text-sm"
            value={categoryFilter}
            onChange={(e) => {
              setCategoryFilter(
                (e.target.value as AdminAssetDto['category']) || '',
              );
              setPage(1);
            }}
            data-testid="admin-assets-category-filter"
          >
            <option value="">全部</option>
            {(
              Object.entries(ADMIN_ASSET_CATEGORY_LABELS) as [
                AdminAssetDto['category'],
                string,
              ][]
            ).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(e) => {
              setIncludeArchived(e.target.checked);
              setPage(1);
            }}
            data-testid="admin-assets-include-archived"
          />
          含已归档
        </label>
      </div>

      {error ? (
        <Card className="border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {error.message}
        </Card>
      ) : null}

      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2">资产编码</th>
              <th className="px-3 py-2">名称</th>
              <th className="px-3 py-2">类别</th>
              <th className="px-3 py-2">状态</th>
              <th className="px-3 py-2">当前保管人</th>
              <th className="px-3 py-2">原值</th>
              <th className="px-3 py-2">采购日期</th>
              <th className="px-3 py-2">操作</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  className="px-3 py-6 text-center text-slate-500"
                  colSpan={8}
                  data-testid="admin-assets-loading"
                >
                  加载中…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td
                  className="px-3 py-6 text-center text-slate-500"
                  colSpan={8}
                  data-testid="admin-assets-empty"
                >
                  暂无资产记录
                </td>
              </tr>
            ) : (
              items.map((it) => (
                <tr key={it.id} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-mono text-xs">{it.assetCode}</td>
                  <td className="px-3 py-2">{it.name}</td>
                  <td className="px-3 py-2">
                    {ADMIN_ASSET_CATEGORY_LABELS[it.category]}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`inline-block rounded px-2 py-0.5 text-xs ${STATUS_BADGE_CLASS[it.status]}`}
                    >
                      {ADMIN_ASSET_STATUS_LABELS[it.status]}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">
                    {it.custodianEmployeeId
                      ? it.custodianEmployeeId.slice(0, 12)
                      : '—'}
                  </td>
                  <td className="px-3 py-2">
                    {fmtMoney(it.originalCost, canSeeFinance)}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {fmtDate(it.purchaseDate)}
                  </td>
                  <td className="px-3 py-2">
                    <Link
                      href={`/admin/assets/${it.id}`}
                      className="text-blue-600 hover:underline"
                      data-testid={`admin-assets-detail-${it.id}`}
                    >
                      详情
                    </Link>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>

      <div className="flex items-center justify-between text-sm text-slate-600">
        <span>
          共 {total} 条 / 第 {page} / {totalPages} 页
        </span>
        <div className="flex gap-2">
          <button
            className="rounded border px-3 py-1 text-sm disabled:opacity-50"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            上一页
          </button>
          <button
            className="rounded border px-3 py-1 text-sm disabled:opacity-50"
            disabled={page >= totalPages || loading}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            下一页
          </button>
        </div>
      </div>
    </div>
  );
}
